import { type CSSProperties, Fragment, useLayoutEffect, useRef, useState } from 'react';

import { Pressable } from '../../primitives/Pressable';
import { View } from '../../primitives/View';
import { cn } from '../../utils/cn';
import { subTestID } from '../../utils/testId';
import { useItemsVersion } from '../../utils/useItemsVersion';
import {
  DEFAULT_ANIMATION_DURATION_MS,
  pillTransitionStyle,
} from '../SegmentedControl/SegmentedControl.animation';
import { tabItemClassName, TabItemContent, tabItemStateOf } from './Tab.parts';
import type { TabProps } from './Tab.types';
import {
  tabItemColumnClass,
  tabItemColumnFullWidthClass,
  tabItemFullWidthClass,
  tabListClass,
  tabListFullWidthClass,
  tabSeparatorClass,
  tabUnderlineClass,
  tabUnderlineSlotClass,
} from './Tab.variants';

interface UnderlineRect {
  x: number;
  y: number;
  w: number;
}

/**
 * Cross-platform single-select tab list — WEB.
 *
 * The active 3px underline (Figma "Select" bar) is a single absolute overlay
 * measured to the active item's box and slid via a CSS transform+width
 * transition — the same "one measured overlay" model as SegmentedControl's
 * active pill (whose animation module this reuses), so the shared package
 * keeps its minimal dependency surface. Honours `prefers-reduced-motion`.
 *
 * Mirrors the Figma "Flashka Design System" `Tab` (file
 * `YZK9r3wDhvw1pY60KlVEIj`; item set 2363:3608 / assembled 2363:4265, dark
 * 2812:44313 / 2812:44404).
 *
 * @example
 * ```tsx
 * import { Tab } from '@flashka/shared/ui';
 * import { FileText, Layers } from '@flashka/shared/ui/icons';
 *
 * const [section, setSection] = useState<'cards' | 'source'>('cards');
 * <Tab
 *   accessibilityLabel="Deck sections"
 *   value={section}
 *   onValueChange={setSection}
 *   items={[
 *     { value: 'cards', label: 'Cards', icon: Layers },
 *     { value: 'source', label: 'Source', icon: FileText },
 *   ]}
 * />
 * ```
 */
export function Tab<V extends string = string>({
  items,
  value,
  onValueChange,
  separator = false,
  fullWidth = false,
  disabled = false,
  // haptic is native-only; accepted for API parity (no-op on web).
  haptic: _haptic = 'selection',
  className,
  itemClassName,
  accessibilityLabel,
  testID,
  itemWebProps,
  itemNativeProps,
  animationDuration,
  animationEasing,
  animationBezier,
  disableAnimation = false,
  // selectionProgress is native-only; accepted for API parity (no-op on web —
  // it is a Reanimated shared value, and `react-native-reanimated` is an
  // optional peer flashka-web does not install). The underline keeps its CSS
  // transition here, which is the right answer anyway: there is no finger to
  // track on a pointer device.
  selectionProgress: _selectionProgress,
}: TabProps<V>) {
  const activeIndex = items.findIndex((i) => i.value === value);
  const itemRefs = useRef<(HTMLElement | null)[]>([]);
  const [underline, setUnderline] = useState<UnderlineRect | null>(null);

  // Measure the active item's content row (row-relative — the tab list is its
  // offsetParent). The underline sits in the 3px slot directly below the
  // content row, so y = offsetTop + offsetHeight. Re-measures when the active
  // item or layout changes; a ResizeObserver on the row catches width changes
  // (label swaps, late web-font metrics). `setUnderline` bails on an
  // unchanged rect so a redundant measure never triggers a render — and deps
  // stay PRIMITIVE (not the `items` array, whose identity changes every
  // render) so the effect can't loop. Same model as SegmentedControl's pill.
  const itemCount = items.length;
  // The item SET, as a primitive dep — see the same dep on SegmentedControl's
  // measure effect: a same-length swap that leaves the active item's index alone
  // would otherwise keep the previous item's measured width.
  // The item SET as a primitive dep, via an allocation-free element-wise
  // compare — a `join()` key would build an array and a string on every
  // render just to discover nothing changed.
  const itemsVersion = useItemsVersion(items);
  useLayoutEffect(() => {
    const measure = () => {
      const el = activeIndex >= 0 ? itemRefs.current[activeIndex] : null;
      if (!el) {
        setUnderline(null);
        return;
      }
      const next = {
        x: el.offsetLeft,
        y: el.offsetTop + el.offsetHeight,
        w: el.offsetWidth,
      };
      setUnderline((prev) =>
        prev && prev.x === next.x && prev.y === next.y && prev.w === next.w ? prev : next,
      );
    };
    measure();
    const row = itemRefs.current[activeIndex]?.offsetParent;
    if (!row || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(row);
    return () => ro.disconnect();
  }, [activeIndex, itemCount, separator, itemsVersion]);

  const underlineTransition: CSSProperties = disableAnimation
    ? { transition: 'none' }
    : pillTransitionStyle(
        animationDuration ?? DEFAULT_ANIMATION_DURATION_MS,
        animationEasing,
        animationBezier,
      );

  return (
    <View
      as="nav"
      accessibilityRole="tablist"
      accessibilityLabel={accessibilityLabel}
      testID={testID}
      className={cn(tabListClass, fullWidth && tabListFullWidthClass, className)}>
      {underline ? (
        <div
          aria-hidden
          className={cn(tabUnderlineClass, 'left-0 top-0 motion-reduce:!transition-none')}
          style={{
            transform: `translate3d(${underline.x}px, ${underline.y}px, 0)`,
            width: underline.w,
            ...underlineTransition,
          }}
        />
      ) : null}
      {items.map((item, index) => {
        const state = tabItemStateOf(item, value, disabled);
        const mergedWebProps = {
          'aria-selected': state.isActive,
          ...(itemWebProps ?? {}),
        };

        return (
          <Fragment key={item.value}>
            {separator && index > 0 ? (
              <View className={tabSeparatorClass} webProps={{ 'aria-hidden': true }} />
            ) : null}
            <View
              className={cn(
                tabItemColumnClass,
                fullWidth && tabItemColumnFullWidthClass,
              )}>
              <Pressable
                ref={(el: HTMLElement | null) => {
                  itemRefs.current[index] = el;
                }}
                disabled={state.disabled}
                onPress={() => onValueChange(item.value)}
                accessibilityRole="tab"
                accessibilityLabel={item.accessibilityLabel}
                testID={item.testID ?? subTestID(testID, `option-${item.value}`)}
                className={tabItemClassName(
                  state,
                  cn(fullWidth && tabItemFullWidthClass, itemClassName),
                )}
                webProps={mergedWebProps}
                nativeProps={itemNativeProps}>
                <TabItemContent item={item} state={state} iconColor="currentColor" />
              </Pressable>
              <View className={tabUnderlineSlotClass} />
            </View>
          </Fragment>
        );
      })}
    </View>
  );
}
(Tab as { displayName?: string }).displayName = 'Tab';
